import { supabase } from '../utils/supabase';

/**
 * Returns a player's full rank history for a given club/season/ladder,
 * ordered oldest-first. Suitable for Elo-over-time charts and rank progression.
 *
 * rank_delta convention: positive = improved (moved up), negative = dropped.
 * NULL on the player's first snapshot for that ladder.
 *
 * @param {string} playerId
 * @param {string} clubId
 * @param {string} seasonId
 * @param {'mixed'|'levels_male'|'levels_female'|'singles_male'|'singles_female'} ladderType
 * @returns {Promise<Array>}
 */
export const getPlayerRankHistory = async (playerId, clubId, seasonId, ladderType) => {
  const { data, error } = await supabase.rpc('get_player_rank_history', {
    p_player_id:   playerId,
    p_club_id:     clubId,
    p_season_id:   seasonId,
    p_ladder_type: ladderType,
  });
  if (error) throw error;
  return data ?? [];
};

/**
 * Returns the ranked entries from the most recent leaderboard snapshot for a
 * club/season/ladder. Includes rank_delta for movement indicators.
 *
 * @param {string} clubId
 * @param {string} seasonId
 * @param {'mixed'|'levels_male'|'levels_female'|'singles_male'|'singles_female'} ladderType
 * @returns {Promise<Array>}
 */
export const getLatestLeaderboardSnapshot = async (clubId, seasonId, ladderType) => {
  const { data, error } = await supabase.rpc('get_latest_leaderboard_snapshot', {
    p_club_id:     clubId,
    p_season_id:   seasonId,
    p_ladder_type: ladderType,
  });
  if (error) throw error;
  return data ?? [];
};

/**
 * Returns the N most recent snapshot headers and their entries for a ladder.
 * Used for active-scoped movement derivation (compare latest two snapshots).
 *
 * @param {string} clubId
 * @param {string} seasonId
 * @param {'mixed'|'levels_male'|'levels_female'|'combined'|'singles_male'|'singles_female'} ladderType
 * @param {number} [limit=2]
 * @returns {Promise<{ current: { snapshot_at: string, entries: Array }|null, previous: { snapshot_at: string, entries: Array }|null }>}
 */
export const getRecentLeaderboardSnapshots = async (clubId, seasonId, ladderType, limit = 2) => {
  const { data: headers, error: headersError } = await supabase
    .from('leaderboard_snapshots')
    .select('id, snapshot_at')
    .eq('club_id', clubId)
    .eq('season_id', seasonId)
    .eq('ladder_type', ladderType)
    .order('snapshot_at', { ascending: false })
    .limit(limit);

  if (headersError) throw headersError;
  if (!headers || headers.length === 0) {
    return { current: null, previous: null };
  }

  const snapshotIds = headers.map((h) => h.id);
  const { data: entries, error: entriesError } = await supabase
    .from('leaderboard_snapshot_entries')
    .select('snapshot_id, player_id, elo_rating, rank, rank_delta, elo_delta')
    .in('snapshot_id', snapshotIds);

  if (entriesError) throw entriesError;

  const entriesBySnapshotId = {};
  snapshotIds.forEach((id) => {
    entriesBySnapshotId[id] = [];
  });
  (entries ?? []).forEach((entry) => {
    if (entriesBySnapshotId[entry.snapshot_id]) {
      entriesBySnapshotId[entry.snapshot_id].push(entry);
    }
  });

  const buildSnapshot = (header) =>
    header
      ? {
          snapshot_at: header.snapshot_at,
          entries: entriesBySnapshotId[header.id] ?? [],
        }
      : null;

  return {
    current: buildSnapshot(headers[0]),
    previous: buildSnapshot(headers[1] ?? null),
  };
};

/**
 * Returns the most recent snapshot entry for a single player on a given ladder.
 * Useful for displaying "you are ranked #X (▲2 since last session)" on profile pages.
 *
 * @param {string} playerId
 * @param {string} clubId
 * @param {string} seasonId
 * @param {'mixed'|'levels_male'|'levels_female'|'singles_male'|'singles_female'} ladderType
 * @returns {Promise<Object|null>}
 */
export const getPlayerCurrentRank = async (playerId, clubId, seasonId, ladderType) => {
  const { data, error } = await supabase
    .from('leaderboard_snapshot_entries')
    .select(`
      elo_rating,
      rank,
      rank_delta,
      elo_delta,
      is_session_participant,
      leaderboard_snapshots!inner (
        session_id,
        snapshot_at,
        ladder_type,
        club_id,
        season_id
      )
    `)
    .eq('player_id', playerId)
    .eq('leaderboard_snapshots.club_id', clubId)
    .eq('leaderboard_snapshots.season_id', seasonId)
    .eq('leaderboard_snapshots.ladder_type', ladderType)
    .order('leaderboard_snapshots(snapshot_at)', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
};

/**
 * Returns all players in the latest snapshot with their rank movements.
 * Convenience wrapper that also maps player_id to readable rank delta display.
 *
 * rank_delta > 0 → moved up   (e.g. +2 = climbed 2 places)
 * rank_delta < 0 → moved down (e.g. -3 = dropped 3 places)
 * rank_delta === null → first snapshot (no prior data)
 *
 * @param {string} clubId
 * @param {string} seasonId
 * @param {'mixed'|'levels_male'|'levels_female'} ladderType
 * @returns {Promise<Array<{player_id, rank, elo_rating, rank_delta, elo_delta, rankMovement}>>}
 */
export const getLeaderboardWithMovement = async (clubId, seasonId, ladderType) => {
  const entries = await getLatestLeaderboardSnapshot(clubId, seasonId, ladderType);
  return entries.map(entry => ({
    ...entry,
    rankMovement: getRankMovementLabel(entry.rank_delta),
  }));
};

/**
 * Returns the latest snapshot row for a single player on a given ladder, using
 * get_player_rank_history (server-ordered ASC by snapshot_at) and taking the last
 * element.  Canonical read for overview recap — do NOT use getLatestLeaderboardSnapshot
 * here (that scans the whole club ladder).
 *
 * Returns null if the player has no snapshot history for this ladder.
 *
 * @param {string} playerId
 * @param {string} clubId
 * @param {string} seasonId
 * @param {'mixed'|'levels_male'|'levels_female'|'singles_male'|'singles_female'} ladderType
 * @returns {Promise<Object|null>}
 */
export const getOverviewRecapLatestRow = async (playerId, clubId, seasonId, ladderType) => {
  const rows = await getPlayerRankHistory(playerId, clubId, seasonId, ladderType);
  return rows.length > 0 ? rows[rows.length - 1] : null;
};

/**
 * Fetches the leaderboard snapshot recap for a specific session and player using
 * exactly two bounded queries — no per-ladder loops.
 *
 * Step 1: fetch snapshot headers where session_id = sessionId  (≤3 rows)
 * Step 2: fetch entries where snapshot_id IN (...) AND player_id = playerId (one query)
 *
 * Returns an array of entries enriched with ladder_type from the parent snapshot.
 * Returns [] if no snapshots exist for this session (old sessions pre-v1).
 *
 * @param {string} sessionId
 * @param {string} playerId
 * @param {string} clubId  Optional — narrows header query if provided
 * @returns {Promise<Array<{ladder_type, rank, rank_delta, elo_delta, session_elo_delta,
 *                          session_wins, session_losses, session_games, is_session_participant,
 *                          snapshot_at}>>}
 */
export const fetchSessionLeaderboardSnapshotRecap = async (sessionId, playerId, clubId) => {
  // Step 1: snapshot headers for this session
  let headersQuery = supabase
    .from('leaderboard_snapshots')
    .select('id, ladder_type, snapshot_at')
    .eq('session_id', sessionId);
  if (clubId) headersQuery = headersQuery.eq('club_id', clubId);

  const { data: headers, error: headersError } = await headersQuery;
  if (headersError) throw headersError;
  if (!headers || headers.length === 0) return [];

  const snapshotIds = headers.map((h) => h.id);
  const ladderBySnapshotId = Object.fromEntries(
    headers.map((h) => [h.id, { ladder_type: h.ladder_type, snapshot_at: h.snapshot_at }])
  );

  // Step 2: entries for this player across all snapshot_ids in one query
  const { data: entries, error: entriesError } = await supabase
    .from('leaderboard_snapshot_entries')
    .select('snapshot_id, rank, rank_delta, elo_rating, elo_delta, session_elo_delta, session_wins, session_losses, session_games, is_session_participant')
    .in('snapshot_id', snapshotIds)
    .eq('player_id', playerId);

  if (entriesError) throw entriesError;
  if (!entries || entries.length === 0) return [];

  return entries.map((entry) => ({
    ...entry,
    ...ladderBySnapshotId[entry.snapshot_id],
  }));
};

/**
 * Session snapshot plus the immediately previous snapshot, per ladder.
 * Entries are the full ladder so callers can re-rank the Active subset.
 * Stored rank / rank_delta on those entries are full-club and are not used here.
 *
 * @param {string} clubId
 * @param {string} sessionId
 * @param {string[]} ladderTypes
 * @returns {Promise<Record<string, { seasonId: string|null, current: { snapshot_at: string, entries: Array }|null, previous: { snapshot_at: string, entries: Array }|null }>>}
 */
export const fetchSessionSnapshotPairs = async (clubId, sessionId, ladderTypes) => {
  const types = [...new Set((ladderTypes || []).filter(Boolean))];
  const empty = {};
  if (!clubId || !sessionId || types.length === 0) return empty;

  const { data: currentHeaders, error: headersError } = await supabase
    .from('leaderboard_snapshots')
    .select('id, snapshot_at, season_id, ladder_type')
    .eq('club_id', clubId)
    .eq('session_id', sessionId)
    .in('ladder_type', types);

  if (headersError) throw headersError;

  const currentByLadder = {};
  (currentHeaders ?? []).forEach((header) => {
    const existing = currentByLadder[header.ladder_type];
    if (!existing || header.snapshot_at > existing.snapshot_at) {
      currentByLadder[header.ladder_type] = header;
    }
  });

  const previousByLadder = {};
  await Promise.all(
    types.map(async (ladderType) => {
      const current = currentByLadder[ladderType];
      if (!current?.season_id) {
        previousByLadder[ladderType] = null;
        return;
      }

      const { data, error } = await supabase
        .from('leaderboard_snapshots')
        .select('id, snapshot_at, season_id, ladder_type')
        .eq('club_id', clubId)
        .eq('season_id', current.season_id)
        .eq('ladder_type', ladderType)
        .lt('snapshot_at', current.snapshot_at)
        .order('snapshot_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      previousByLadder[ladderType] = data?.[0] ?? null;
    })
  );

  const snapshotIds = [
    ...Object.values(currentByLadder),
    ...Object.values(previousByLadder),
  ]
    .filter(Boolean)
    .map((header) => header.id);

  let entries = [];
  if (snapshotIds.length > 0) {
    const { data, error } = await supabase
      .from('leaderboard_snapshot_entries')
      .select('snapshot_id, player_id, elo_rating, rank, rank_delta, elo_delta')
      .in('snapshot_id', snapshotIds);

    if (error) throw error;
    entries = data ?? [];
  }

  const entriesBySnapshotId = {};
  entries.forEach((entry) => {
    if (!entriesBySnapshotId[entry.snapshot_id]) {
      entriesBySnapshotId[entry.snapshot_id] = [];
    }
    entriesBySnapshotId[entry.snapshot_id].push(entry);
  });

  const toSnapshot = (header) =>
    header
      ? {
          snapshot_at: header.snapshot_at,
          entries: entriesBySnapshotId[header.id] ?? [],
        }
      : null;

  const result = {};
  types.forEach((ladderType) => {
    const current = currentByLadder[ladderType] ?? null;
    result[ladderType] = {
      seasonId: current?.season_id ?? null,
      current: toSnapshot(current),
      previous: toSnapshot(previousByLadder[ladderType]),
    };
  });
  return result;
};

/**
 * Maps a stored rank_delta number to a human-readable direction label.
 *
 * @param {number|null} rankDelta
 * @returns {'up'|'down'|'same'|'new'}
 */
export const getRankMovementLabel = (rankDelta) => {
  if (rankDelta === null || rankDelta === undefined) return 'new';
  if (rankDelta > 0) return 'up';
  if (rankDelta < 0) return 'down';
  return 'same';
};
